import type { Build } from '@/api/schemas';
import { formatDateTime, timeUntil } from '@/lib/format';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { Tag } from '@/components/badges';
import { Card, CardContent } from '@/components/ui/card';
import { attachDeadline, deadlineExpired, executionFields, type ClientExecution } from './awaitingClientModel';

/**
 * `awaiting_client` (ci_client mode): the SDK inside the customer's CI must run the provider command and attach the
 * provider build id before the deadline, or the build expires. The runtime token is never shown here — the API hands it
 * to the SDK exactly once and the console does not model it.
 */
export function AwaitingClientPanel({ build: b, now }: { build: Build; now: number }) {
  const deadline = attachDeadline(b);
  return (
    <Card data-testid="build-awaiting-client" className="enter border-awaiting/40 bg-awaiting/5">
      <CardContent className="flex flex-col gap-3 border-t-0">
        <div className="flex flex-wrap items-center gap-3">
          <AwaitingIntro slotHeld={b.slotHeld} hasDeadline={Boolean(deadline)} />
          {deadline ? <AttachDeadline deadline={deadline} now={now} /> : null}
        </div>
        {b.clientExecution ? <ClientExecutionList exec={b.clientExecution} /> : null}
        <p className="flex items-center gap-1.5 text-xs text-subtle">
          <Icon name="lock" size={14} />
          The runtime token was delivered to the SDK once and is never shown here.
          <Tag>{b.statusReason ?? 'awaiting_client'}</Tag>
        </p>
      </CardContent>
    </Card>
  );
}

function AwaitingIntro({ slotHeld, hasDeadline }: { slotHeld: boolean | null | undefined; hasDeadline: boolean }) {
  return (
    <>
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-awaiting/15 text-awaiting">
        <Icon name="hourglass_empty" size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <Kicker className="text-awaiting">Waiting for your CI</Kicker>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          {slotHeld ? 'A build slot is reserved for this build.' : 'Waiting for a free build slot.'} Your CI must start the build with hangar build{hasDeadline ? ' before the time runs out' : ''}.
        </p>
      </div>
    </>
  );
}

function AttachDeadline({ deadline, now }: { deadline: string; now: number }) {
  const expired = deadlineExpired(deadline, now);
  return (
    <div className="text-right">
      <div className="text-xs text-subtle">Time left</div>
      <div data-testid="build-attach-countdown" className={`tabular font-mono text-lg font-semibold ${expired ? 'text-failed' : 'text-foreground'}`}>
        {expired ? 'Time is up' : timeUntil(deadline, now)}
      </div>
      <div className="font-mono text-[11px] text-subtle">{formatDateTime(deadline)}</div>
    </div>
  );
}

function ClientExecutionList({ exec }: { exec: ClientExecution }) {
  return (
    <dl className="grid gap-x-6 gap-y-1 font-mono text-xs sm:grid-cols-2">
      {executionFields(exec).map((f) => (
        <div key={f.label} className={f.wrapperClass}>
          <dt className="text-subtle">{f.label}</dt>
          <dd className={f.valueClass}>{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}
